/**
 * Structure-derived template preview — phase names + task density from the
 * real template definition (TEMPLATE-CENTER-REDESIGN-1).
 *
 * Chosen over screenshots: never stale, never claims a UI surface the product
 * does not deliver, and each card differs because structure differs.
 * Methodology-only placeholder micros (board/timeline/list) are retired.
 */
import type { TemplateDto } from '../templates.api';
import { templatePhaseList } from '../template.mapper';

interface Props {
  template: Pick<
    TemplateDto,
    'methodology' | 'phases' | 'taskTemplates' | 'task_templates' | 'name'
  > & {
    kind?: TemplateDto['kind'];
    templateCode?: string | null;
  };
  className?: string;
  /** "card" = small thumbnail in the grid; "preview" = larger modal hero */
  size?: 'card' | 'preview';
}

function taskCount(t: Props['template']): number {
  const flat = (t.taskTemplates ?? t.task_templates ?? []) as unknown[];
  return Array.isArray(flat) ? flat.length : 0;
}

function tasksInPhase(
  template: Props['template'],
  phaseOrder: number,
): number {
  const flat = (template.taskTemplates ?? template.task_templates ?? []) as Array<{
    phaseOrder?: number;
  }>;
  if (!Array.isArray(flat)) return 0;
  return flat.filter((row) => (row.phaseOrder ?? 0) === phaseOrder).length;
}

const PHASE_ACCENTS = [
  'bg-blue-500',
  'bg-indigo-500',
  'bg-sky-500',
  'bg-cyan-600',
  'bg-slate-500',
] as const;

export function TemplateBlueprint({ template, className, size = 'card' }: Props) {
  const phases = templatePhaseList(template as TemplateDto);
  const tk = taskCount(template);
  const m = template.methodology;
  const ariaParts: string[] = [];
  if (phases.length > 0) {
    ariaParts.push(
      `${phases.length} phase${phases.length === 1 ? '' : 's'}: ${phases
        .map((p) => p.name)
        .slice(0, 5)
        .join(', ')}`,
    );
  }
  if (tk > 0) ariaParts.push(`${tk} task${tk === 1 ? '' : 's'}`);
  if (ariaParts.length === 0) ariaParts.push('Empty structure preview');
  const ariaLabel = ariaParts.join(' · ');

  const heightClass = size === 'card' ? 'h-28' : 'h-44';
  const maxPhases = size === 'card' ? 4 : 6;
  const visible = phases.slice(0, maxPhases);
  const overflow = Math.max(0, phases.length - visible.length);
  const maxTasksInVisible = Math.max(
    1,
    ...visible.map((p) => tasksInPhase(template, p.order ?? 0)),
    Math.ceil(tk / Math.max(phases.length, 1)),
  );

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={`${heightClass} w-full overflow-hidden bg-slate-100 p-2.5 ${className ?? ''}`}
      data-testid="template-blueprint"
      data-methodology={m ?? 'unspecified'}
      data-structure-preview="phases"
    >
      {visible.length === 0 ? (
        <EmptyStructure taskCount={tk} />
      ) : (
        <div className="flex h-full w-full flex-col justify-center gap-1.5 rounded-md bg-white px-2 py-1.5 shadow-sm ring-1 ring-slate-200/70">
          {visible.map((phase, index) => {
            const count = tasksInPhase(template, phase.order ?? 0);
            const density =
              count > 0
                ? Math.max(0.18, Math.min(1, count / maxTasksInVisible))
                : phases.length > 0
                  ? Math.max(0.15, Math.min(0.55, tk / (phases.length * maxTasksInVisible)))
                  : 0.2;
            return (
              <div
                key={`${phase.name}-${index}`}
                className="flex min-w-0 items-center gap-2"
                data-testid={`template-blueprint-phase-${index}`}
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${PHASE_ACCENTS[index % PHASE_ACCENTS.length]}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-[10px] font-medium leading-tight text-slate-700">
                  {phase.name}
                </span>
                <span
                  className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-slate-100"
                  aria-hidden
                >
                  <span
                    className={`block h-full rounded-full ${PHASE_ACCENTS[index % PHASE_ACCENTS.length]} opacity-80`}
                    style={{ width: `${Math.round(density * 100)}%` }}
                  />
                </span>
              </div>
            );
          })}
          {overflow > 0 ? (
            <div className="pl-3.5 text-[9px] font-medium text-slate-400">
              +{overflow} more phase{overflow === 1 ? '' : 's'}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function EmptyStructure({ taskCount }: { taskCount: number }) {
  return (
    <div className="flex h-full w-full flex-col justify-center gap-1.5 rounded-md bg-white px-2 py-1.5 shadow-sm ring-1 ring-slate-200/70">
      <div className="h-1.5 w-2/3 rounded bg-slate-200" />
      <div className="h-1.5 w-1/2 rounded bg-slate-100" />
      <div className="h-1.5 w-3/5 rounded bg-slate-100" />
      {taskCount > 0 ? (
        <p className="mt-1 text-[9px] font-medium text-slate-400">
          {taskCount} starter task{taskCount === 1 ? '' : 's'}
        </p>
      ) : (
        <p className="mt-1 text-[9px] font-medium text-slate-400">Starter structure</p>
      )}
    </div>
  );
}
